import React from 'react';
import PropTypes from 'prop-types';
import { MdAutoAwesome } from 'react-icons/md';

const ChatWindow = ({ conversation, onEditMessage, isTyping, theme }) => {
  // ✅ Detect if text is a screenplay script
  const isScriptContent = (text) => {
    if (!text) return false;
    
    // Check for screenplay markers
    const scriptMarkers = [
      /\*\*Title:\*\*/i,
      /\*\*Genre:\*\*/i,
      /\*\*SCENE\s+\d+:\*\*/i,
      /INT\.\s+[A-Z]/,
      /EXT\.\s+[A-Z]/,
      /\*\*Act\s+\d+:\*\*/i,
      /FADE IN/i,
      /FADE OUT/i,
      /CUT TO:/i
    ];
    
    return scriptMarkers.some(marker => marker.test(text));
  };

  // ✅ Format screenplay content
  const formatScreenplayContent = (text) => {
    if (!text) return null;

    const lines = text.split('\n');
    const elements = [];
    let currentIndex = 0;

    lines.forEach((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return;

      // Remove **markdown** styling for processing
      const cleanLine = trimmed.replace(/\*\*/g, '');

      // Title (starts with **Title:**)
      if (/^\*\*Title:\*\*/.test(trimmed)) {
        const title = cleanLine.replace(/^Title:\s*/i, '');
        elements.push(
          <div key={`title-${i}`} className="pb-script-title">
            {title}
          </div>
        );
      }
      // Genre
      else if (/^\*\*Genre:\*\*/.test(trimmed)) {
        const genre = cleanLine.replace(/^Genre:\s*/i, '');
        elements.push(
          <div key={`genre-${i}`} className="pb-script-genre">
            Genre: {genre}
          </div>
        );
      }
      // Logline
      else if (/^\*\*Logline:\*\*/.test(trimmed)) {
        const logline = cleanLine.replace(/^Logline:\s*/i, '');
        elements.push(
          <div key={`logline-${i}`} className="pb-script-logline">
            {logline}
          </div>
        );
      }
      // Act headings
      else if (/^\*\*Act\s+\d+:\*\*/.test(trimmed)) {
        const act = cleanLine;
        elements.push(
          <div key={`act-${i}`} className="pb-script-act">
            {act}
          </div>
        );
      }
      // Scene headings
      else if (/^\*\*SCENE\s+\d+:\*\*/.test(trimmed)) {
        const scene = cleanLine;
        elements.push(
          <div key={`scene-${i}`} className="pb-script-scene-heading">
            {scene}
          </div>
        );
      }
      // INT/EXT scene headings
      else if (/^(INT\.|EXT\.)\s+[A-Z]/.test(cleanLine)) {
        elements.push(
          <div key={`location-${i}`} className="pb-script-location">
            {cleanLine}
          </div>
        );
      }
      // Character names (all caps, short, followed by dialogue)
      else if (
        cleanLine === cleanLine.toUpperCase() &&
        cleanLine.length > 0 &&
        cleanLine.length < 30 &&
        !/^(FADE|CUT TO|DISSOLVE|INT\.|EXT\.)/.test(cleanLine) &&
        !cleanLine.match(/^\d/) &&
        i < lines.length - 1
      ) {
        elements.push(
          <div key={`character-${i}`} className="pb-script-character">
            {cleanLine}
          </div>
        );
      }
      // Parenthetical (in parentheses)
      else if (cleanLine.startsWith('(') && cleanLine.endsWith(')')) {
        elements.push(
          <div key={`paren-${i}`} className="pb-script-parenthetical">
            {cleanLine}
          </div>
        );
      }
      // Transitions
      else if (/^(FADE|CUT TO|DISSOLVE|SMASH CUT)/.test(cleanLine)) {
        elements.push(
          <div key={`trans-${i}`} className="pb-script-transition">
            {cleanLine}
          </div>
        );
      }
      // Dialogue or action
      else {
        // Check if previous element was character name
        const prevElement = elements[elements.length - 1];
        const isDialogue = prevElement && prevElement.props.className === 'pb-script-character';
        
        elements.push(
          <div 
            key={`text-${i}`} 
            className={isDialogue ? 'pb-script-dialogue' : 'pb-script-action'}
          >
            {cleanLine}
          </div>
        );
      }
    });

    return <div className="pb-script-content">{elements}</div>;
  };

  // ✅ Format normal conversation text
  const formatNormalText = (text) => {
    if (!text) return null;
    
    // Basic formatting: preserve line breaks
    return text.split('\n').map((line, i) => (
      <React.Fragment key={i}>
        {line}
        {i < text.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  if (!conversation || !conversation.messages) {
    return (
      <div className={`pb-chat-window pb-chat-theme-${theme}`}>
        <div className="pb-chat-empty">
          <MdAutoAwesome size={48} style={{ opacity: 0.3 }} />
          <p>Start a conversation to generate your script</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* Chat Window Container */
        .pb-chat-window {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .pb-chat-theme-light {
          background-color: #f9fafb;
        }

        .pb-chat-theme-dark {
          background-color: #111827;
        }

        .pb-chat-theme-read {
          background-color: #fef3c7;
        }

        .pb-chat-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          opacity: 0.5;
          gap: 16px;
        }

        .pb-chat-empty p {
          font-size: 16px;
        }

        /* Message Bubbles */
        .pb-message-container {
          display: flex;
          gap: 12px;
          animation: pb-message-fade-in 0.3s ease-out;
        }

        @keyframes pb-message-fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .pb-message-container.pb-from-me {
          flex-direction: row-reverse;
        }

        .pb-message-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          flex-shrink: 0;
          margin-top: 4px;
        }

        .pb-chat-theme-light .pb-message-avatar {
          background-color: #e5e7eb;
          color: #6b7280;
        }

        .pb-chat-theme-dark .pb-message-avatar {
          background-color: #374151;
          color: #9ca3af;
        }

        .pb-chat-theme-read .pb-message-avatar {
          background-color: #fde68a;
          color: #92400e;
        }

        .pb-message-avatar.pb-ai-avatar {
          background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
          color: white;
        }

        .pb-message-content {
          max-width: 75%;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .pb-message-bubble {
          padding: 12px 16px;
          border-radius: 12px;
          position: relative;
        }

        /* User Messages */
        .pb-message-container.pb-from-me .pb-message-bubble {
          background-color: #14b8a6;
          color: white;
          border-bottom-right-radius: 4px;
        }

        /* AI Messages - Normal */
        .pb-message-container:not(.pb-from-me) .pb-message-bubble:not(.pb-script-message) {
          border-bottom-left-radius: 4px;
        }

        .pb-chat-theme-light .pb-message-container:not(.pb-from-me) .pb-message-bubble:not(.pb-script-message) {
          background-color: white;
          color: #111827;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .pb-chat-theme-dark .pb-message-container:not(.pb-from-me) .pb-message-bubble:not(.pb-script-message) {
          background-color: #1f2937;
          color: #f9fafb;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
        }

        .pb-chat-theme-read .pb-message-container:not(.pb-from-me) .pb-message-bubble:not(.pb-script-message) {
          background-color: #fef3c7;
          color: #78350f;
          border: 1px solid #fcd34d;
        }

        /* ✅ AI Messages - Script Format */
        .pb-script-message {
          background-color: #ffffff !important;
          border: 2px solid #14b8a6;
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 4px 20px rgba(20, 184, 166, 0.15);
          max-width: 90%;
          position: relative;
          overflow: hidden;
        }

        .pb-chat-theme-dark .pb-script-message {
          background-color: #1e293b !important;
          border-color: #14b8a6;
          box-shadow: 0 4px 20px rgba(20, 184, 166, 0.3);
        }

        /* ✅ Sparkle Animation Badge */
        .pb-script-badge {
          position: absolute;
          top: 12px;
          right: 12px;
          background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
          color: white;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 6px;
          animation: pb-badge-pulse 2s infinite;
        }

        @keyframes pb-badge-pulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 2px 8px rgba(20, 184, 166, 0.3);
          }
          50% {
            transform: scale(1.05);
            box-shadow: 0 4px 16px rgba(20, 184, 166, 0.5);
          }
        }

        .pb-script-badge-icon {
          animation: pb-sparkle-rotate 3s linear infinite;
        }

        @keyframes pb-sparkle-rotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* ✅ Screenplay Formatting */
        .pb-script-content {
          font-family: 'Courier New', Courier, monospace;
          line-height: 1.6;
          color: #1f2937;
        }

        .pb-chat-theme-dark .pb-script-content {
          color: #e5e7eb;
        }

        .pb-script-title {
          font-size: 24px;
          font-weight: 700;
          text-align: center;
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #14b8a6;
        }

        .pb-script-genre {
          text-align: center;
          font-size: 14px;
          margin-bottom: 16px;
          opacity: 0.8;
          font-style: italic;
        }

        .pb-script-logline {
          font-size: 13px;
          line-height: 1.8;
          margin: 16px 0 24px 0;
          padding: 12px 16px;
          background-color: rgba(20, 184, 166, 0.05);
          border-left: 3px solid #14b8a6;
          border-radius: 4px;
          font-style: italic;
        }

        .pb-script-act {
          font-size: 18px;
          font-weight: 700;
          text-align: center;
          margin: 32px 0 24px 0;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          padding: 12px;
          background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
          color: white;
          border-radius: 8px;
        }

        .pb-script-scene-heading {
          font-size: 16px;
          font-weight: 700;
          margin: 24px 0 16px 0;
          text-transform: uppercase;
          color: #14b8a6;
          letter-spacing: 0.5px;
        }

        .pb-script-location {
          font-weight: 700;
          margin: 16px 0 12px 0;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .pb-script-character {
          text-transform: uppercase;
          margin: 16px 0 4px 0;
          text-align: center;
          font-weight: 700;
          color: #0d9488;
        }

        .pb-script-parenthetical {
          margin: 2px 0;
          text-align: center;
          font-style: italic;
          opacity: 0.9;
        }

        .pb-script-dialogue {
          margin: 4px 0 12px 0;
          padding-left: 40px;
          padding-right: 40px;
          line-height: 1.5;
        }

        .pb-script-action {
          margin: 12px 0;
          line-height: 1.7;
        }

        .pb-script-transition {
          text-align: right;
          font-weight: 700;
          text-transform: uppercase;
          margin: 16px 0;
          opacity: 0.8;
        }

        /* Message Timestamp */
        .pb-message-timestamp {
          font-size: 11px;
          opacity: 0.6;
          margin-top: 4px;
        }

        .pb-message-container.pb-from-me .pb-message-timestamp {
          text-align: right;
        }

        /* ✅ Typing Indicator with Sparkles */
        .pb-typing-indicator {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          border-radius: 12px;
          width: fit-content;
        }

        .pb-chat-theme-light .pb-typing-indicator {
          background-color: white;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .pb-chat-theme-dark .pb-typing-indicator {
          background-color: #1f2937;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
        }

        .pb-chat-theme-read .pb-typing-indicator {
          background-color: #fef3c7;
          border: 1px solid #fcd34d;
        }

        .pb-typing-dots {
          display: flex;
          gap: 4px;
        }

        .pb-typing-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #14b8a6;
          animation: pb-typing-bounce 1.4s infinite ease-in-out;
        }

        .pb-typing-dot:nth-child(2) {
          animation-delay: 0.2s;
        }

        .pb-typing-dot:nth-child(3) {
          animation-delay: 0.4s;
        }

        @keyframes pb-typing-bounce {
          0%, 60%, 100% {
            transform: translateY(0);
          }
          30% {
            transform: translateY(-10px);
          }
        }

        .pb-typing-sparkle {
          color: #14b8a6;
          font-size: 20px;
          animation: pb-sparkle-pulse 1.5s infinite;
        }

        @keyframes pb-sparkle-pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1) rotate(0deg);
          }
          50% {
            opacity: 0.6;
            transform: scale(1.2) rotate(180deg);
          }
        }

        .pb-typing-text {
          font-size: 13px;
          font-weight: 500;
          color: #14b8a6;
        }

        /* Scrollbar */
        .pb-chat-window::-webkit-scrollbar {
          width: 8px;
        }

        .pb-chat-window::-webkit-scrollbar-track {
          background: transparent;
        }

        .pb-chat-window::-webkit-scrollbar-thumb {
          background: rgba(20, 184, 166, 0.3);
          border-radius: 4px;
        }

        .pb-chat-window::-webkit-scrollbar-thumb:hover {
          background: rgba(20, 184, 166, 0.5);
        }
      `}</style>

      <div className={`pb-chat-window pb-chat-theme-${theme}`}>
        {conversation.messages.map((message, index) => {
          const isScript = message.isScript || isScriptContent(message.text);
          
          return (
            <div
              key={index}
              className={`pb-message-container ${message.fromMe ? 'pb-from-me' : ''}`}
            >
              <div className="pb-message-avatar pb-ai-avatar">
                {message.fromMe ? '👤' : <MdAutoAwesome />}
              </div>
              
              <div className="pb-message-content">
                <div className={`pb-message-bubble ${isScript && !message.fromMe ? 'pb-script-message' : ''}`}>
                  {isScript && !message.fromMe && (
                    <div className="pb-script-badge">
                      <MdAutoAwesome className="pb-script-badge-icon" />
                      SCREENPLAY
                    </div>
                  )}
                  
                  {isScript && !message.fromMe ? (
                    formatScreenplayContent(message.text)
                  ) : (
                    formatNormalText(message.text)
                  )}
                </div>
                
                <div className="pb-message-timestamp">
                  {new Date(message.timestamp).toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </div>
              </div>
            </div>
          );
        })}

        {/* ✅ Typing Indicator with Sparkles */}
        {isTyping && (
          <div className="pb-message-container">
            <div className="pb-message-avatar pb-ai-avatar">
              <MdAutoAwesome />
            </div>
            
            <div className="pb-typing-indicator">
              <MdAutoAwesome className="pb-typing-sparkle" />
              <div className="pb-typing-dots">
                <div className="pb-typing-dot"></div>
                <div className="pb-typing-dot"></div>
                <div className="pb-typing-dot"></div>
              </div>
              <span className="pb-typing-text">Generating screenplay...</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

ChatWindow.propTypes = {
  conversation: PropTypes.shape({
    messages: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        fromMe: PropTypes.bool.isRequired,
        timestamp: PropTypes.string.isRequired,
        isScript: PropTypes.bool
      })
    )
  }),
  onEditMessage: PropTypes.func,
  isTyping: PropTypes.bool,
  theme: PropTypes.string
};

export default ChatWindow;
